/**
 * Confirm Talent Payment (prototype v108 JSON "Pay Talent"; H§6, H§10): Client, Talent, Role, Event Date, the
 * calculated rate, deductions from the deduction ledger, Amount Due and Pay By; Billing enters the Amount Paid and
 * confirms. The confirm is the same endpoint the current Billing dashboard uses (/api/payments/confirm-talent-payment),
 * so the booking's paid flag, amount and the Talent Paid stage move the same way.
 *
 * Pay notes (Kendra, 30 Oct 2026: everything that changed this talent's pay in one place, so she doesn't open the
 * booking): the rate, hours and travel/flat set on the booking, deductions, and the deduction flags Editing and Talent
 * raised (Traci's Loom, 28 Sep 2026). Billing settles each flag here before paying: assess an amount or dismiss it.
 */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/admin/ui/button';
import { Input } from '@/components/admin/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/admin/ui/dialog';
import { stagingFetch, shortDate, mountainToday } from '../api';

export type PayRow = { booking_id: string; client_name?: string | null; event_date?: string | null; talent_name?: string | null; role?: string; role_label?: string; pay_by?: string | null };

const usd = (cents: number) => `$${(Number(cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PayTalentDialog({ row, onClose, onPaid }: { row: PayRow | null; onClose: () => void; onPaid: () => void }) {
  const [pay, setPay] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [settle, setSettle] = useState<Record<string, { reason_key: string; amount: string }>>({});
  const [reasons, setReasons] = useState<any[]>([]);

  const loadPay = () => {
    if (!row) return;
    stagingFetch(`/api/admin/staging/bookings/${row.booking_id}/deductions`)
      .then((d) => {
        const p = (d.pay || []).find((x: any) => x.role === row.role) || null;
        setPay(p);
        setReasons(d.reasons || []);
        if (p) setAmount((p.due_cents / 100).toFixed(2));
      })
      .catch((e) => setError(e.message || String(e)));
  };
  useEffect(() => {
    setPay(null); setError(null); setAmount(''); setSettle({});
    loadPay();
  }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (n: any, action: 'assess' | 'dismiss') => {
    const s = settle[n.flag_id] || { reason_key: n.reason_keys?.[0] || '', amount: '' };
    setBusy(true);
    try {
      await stagingFetch(`/api/admin/staging/deduction-flags/${n.flag_id}/resolve`, { method: 'POST', body: JSON.stringify(action === 'assess' ? { action, reason_key: s.reason_key, amount: s.amount } : { action }) });
      toast.success(action === 'assess' ? `Deduction of $${Number(s.amount).toFixed(2)} assessed` : 'Flag dismissed');
      loadPay();
    } catch (e: any) { toast.error(e.message || String(e)); } finally { setBusy(false); }
  };
  const openFlags = (pay?.notes || []).filter((n: any) => n.kind === 'flag');

  const confirm = async () => {
    if (!row) return;
    const cents = Math.round(Number(amount) * 100);
    if (!Number.isFinite(cents) || cents < 0) { toast.error('Enter the amount paid'); return; }
    setBusy(true);
    try {
      await stagingFetch('/api/payments/confirm-talent-payment', { method: 'POST', body: JSON.stringify({ bookingId: row.booking_id, talentRole: row.role, amountCents: cents }) });
      toast.success(`${row.talent_name || 'Talent'} paid ${usd(cents)}`);
      onPaid();
    } catch (e: any) { toast.error(e.message || String(e)); } finally { setBusy(false); }
  };

  const late = row?.pay_by && row.pay_by < mountainToday();
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Confirm Talent Payment</DialogTitle>
          <DialogDescription>Media approved. Record what was paid; the booking moves to Talent Paid once every talent on it is paid.</DialogDescription>
        </DialogHeader>
        {row && (
          <dl className="grid grid-cols-[130px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
            <dt className="text-muted-foreground">Client</dt><dd>{row.client_name || '—'}</dd>
            <dt className="text-muted-foreground">Talent</dt><dd>{row.talent_name || pay?.name || '—'}</dd>
            <dt className="text-muted-foreground">Role</dt><dd>{row.role_label || row.role}</dd>
            <dt className="text-muted-foreground">Event Date</dt><dd>{shortDate(row.event_date)}</dd>
            <dt className="text-muted-foreground">Pay By</dt><dd className={late ? 'font-semibold text-destructive' : ''}>{row.pay_by ? `${shortDate(row.pay_by)}${late ? ' · past due' : ''}` : 'Media not uploaded'}</dd>
            {pay && (<>
              <dt className="text-muted-foreground">Calculated Rate</dt><dd>{pay.hours}h × ${pay.hourly_rate}{pay.rate_source === 'booking' ? " (this booking's rate)" : ''}{pay.flat_cents ? <> = {usd(pay.hourly_cents)} + {usd(pay.flat_cents)}{pay.flat_reason ? ` ${pay.flat_reason}` : ''}</> : null} = {usd(pay.original_cents)}</dd>
              <dt className="text-muted-foreground">Deductions</dt><dd>{pay.deducted_cents ? `− ${usd(pay.deducted_cents)}` : 'None'}</dd>
              <dt className="font-semibold">Amount Due</dt><dd className="font-semibold">{usd(pay.due_cents)}</dd>
            </>)}
          </dl>
        )}
        {pay?.notes?.length > 0 && (
          <div className="space-y-1.5 rounded-md border p-2 text-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pay notes</p>
            {pay.notes.filter((n: any) => n.kind !== 'flag').map((n: any, i: number) => (
              <p key={i}>{n.text}{n.note ? ` · ${n.note}` : ''}{n.by ? <span className="text-xs text-muted-foreground"> · {n.by}{n.at ? ` ${shortDate(n.at)}` : ''}</span> : null}</p>
            ))}
            {openFlags.map((n: any) => {
              const s = settle[n.flag_id] || { reason_key: n.reason_keys?.[0] || '', amount: '' };
              return (
                <div key={n.flag_id} className="space-y-1.5 rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-950">
                  <p><b>{n.text}</b> · {n.note}<span className="block text-xs opacity-80">{n.by || 'Staff'}{n.at ? ` · ${shortDate(n.at)}` : ''}</span></p>
                  <div className="flex flex-wrap items-center gap-2">
                    <select aria-label="Deduction reason" className="h-8 rounded-md border bg-background px-2 text-xs" value={s.reason_key} onChange={(e) => setSettle({ ...settle, [n.flag_id]: { ...s, reason_key: e.target.value } })}>
                      {reasons.filter((r: any) => (n.reason_keys || []).includes(r.key)).map((r: any) => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                    <Input aria-label="Deduction amount" className="h-8 w-24" type="number" min="0" step="0.01" placeholder="0.00" value={s.amount} onChange={(e) => setSettle({ ...settle, [n.flag_id]: { ...s, amount: e.target.value } })} />
                    <Button size="sm" className="h-8" disabled={busy || !(Number(s.amount) > 0)} onClick={() => resolve(n, 'assess')}>Assess</Button>
                    <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => resolve(n, 'dismiss')}>Dismiss</Button>
                  </div>
                  {reasons.find((r: any) => r.key === s.reason_key)?.guidance && <p className="text-[11px] opacity-80">{reasons.find((r: any) => r.key === s.reason_key).guidance}</p>}
                </div>
              );
            })}
          </div>
        )}
        {!pay && !error && row && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading the payment…</div>}
        {error && <p className="text-sm text-destructive">Could not load the payment: {error}</p>}
        <label className="block text-sm">Amount Paid ($)<Input aria-label="Amount paid" type="number" min="0" step="0.01" className="mt-1" value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
          <Button onClick={confirm} disabled={busy || amount === '' || Number(amount) < 0}>{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Confirm Payment</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
